import { Alert, Box, Button, Paper, Typography } from '@mui/material';
import { SpecFieldEditor } from './SpecFieldEditor';
import type { SpecField } from '../grid/types/specificationTypes';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import type { SerializedError } from '@reduxjs/toolkit';

type SpecificationTypePanelProps = {
  fields: SpecField[];
  fieldValues: Record<string, string>;
  onFieldChange: (key: string, value: string) => void;
  onSave: () => void;
  isSaving: boolean;
  saveSuccess: boolean;
  saveError?: FetchBaseQueryError | SerializedError;
};

export const SpecificationTypePanel = ({
  fields,
  fieldValues,
  onFieldChange,
  onSave,
  isSaving,
  saveSuccess,
  saveError,
}: SpecificationTypePanelProps) => {
  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, md: 3 },
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {fields.map((field) => (
          <Box
            key={field.key}
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '200px 1fr' },
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Typography variant="body2" fontWeight={500} sx={{ color: 'text.secondary' }}>
              {field.caption}
              {field.isRequired && (
                <Box component="span" sx={{ color: 'error.main', ml: 0.5 }}>
                  *
                </Box>
              )}
            </Typography>

            <SpecFieldEditor
              field={field}
              value={fieldValues[field.key] ?? ''}
              onChange={(v) => onFieldChange(field.key, v)}
            />
          </Box>
        ))}
      </Box>

      <Box sx={{ mt: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button variant="contained" size="small" onClick={onSave} disabled={isSaving}>
          {isSaving ? 'Saving…' : 'Save'}
        </Button>

        {saveSuccess && (
          <Alert severity="success" sx={{ py: 0 }}>
            Saved
          </Alert>
        )}
        {saveError && (
          <Alert severity="error" sx={{ py: 0 }}>
            Failed to save specifications.
          </Alert>
        )}
      </Box>
    </Paper>
  );
};
